import { map } from 'rxjs/operators';
import { HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
  addToPayloadIfNotNull,
  AjaxData,
  AjaxService,
  blankHttpParamsWithCustomEncoder,
  formUrlEncodedHttpOptions,
} from '../../core/ajax.service';
import { reviewTypeMap, Workflow } from '../../common/review-types';
import { ApiRoutesV1 } from '../../common/routes';

@Injectable()
export class EmailWorkflowService {
  constructor(private readonly ajaxService: AjaxService) {}

  getEmailWorkflow(): Observable<Workflow> {
    return this.ajaxService.get(ApiRoutesV1.settingsEmailWorkflow).pipe(
      map((response: HttpResponse<AjaxData>) => {
        if (response.body.data.email_workflow === Workflow.Internal.toString()) {
          return Workflow.Internal;
        } else if (response.body.data.email_workflow === Workflow.External.toString()) {
          return Workflow.External;
        }
        throw new Error(`Workflow '${response.body.data.email_workflow}' isn't supported`);
      }),
    );
  }

  updateEmailWorkflow(emailWorkflow: Workflow): Observable<HttpResponse<AjaxData>> {
    const payload: HttpParams = blankHttpParamsWithCustomEncoder.set('emailWorkflow', emailWorkflow.toString());
    return this.ajaxService.post(ApiRoutesV1.settingsEmailWorkflow, payload, formUrlEncodedHttpOptions);
  }

  getEmailSettings(emailType: Workflow): Observable<EmailSettings> {
    return this.ajaxService
      .get(ApiRoutesV1.settingsEmailGet, {
        params: {
          emailType: reviewTypeMap[emailType],
        },
      })
      .pipe(map((response: HttpResponse<AjaxData>) => mapResponseToEmailSetting(response, emailType)));
  }

  saveEmailSettings(
    emailSettings: EmailSettings,
    reviewGenType: string,
    headerUrl: string,
    logoUrl: string,
  ): Observable<HttpResponse<AjaxData>> {
    let addPayload: HttpParams = blankHttpParamsWithCustomEncoder;
    addPayload = addPayload.set('emailType', reviewTypeMap[reviewGenType]);
    addPayload = addPayload.set('emailGreeting', emailSettings.emailGreeting);
    addPayload = addToPayloadIfNotNull('messageBody', emailSettings.emailBody, addPayload);
    addPayload = addToPayloadIfNotNull('secondaryMessageBody', emailSettings.secondaryMessageBody, addPayload);
    addPayload = addPayload.set('emailSubject', emailSettings.emailSubject);
    addPayload = addToPayloadIfNotNull('senderEmail', emailSettings.sendFromAddress, addPayload);
    addPayload = addPayload.set('emailTitle', emailSettings.emailTitle);
    addPayload = addToPayloadIfNotNull('logoUrl', logoUrl, addPayload);
    addPayload = addToPayloadIfNotNull('headerUrl', headerUrl, addPayload);
    addPayload = addPayload.set('emailTitleFont', emailSettings.emailTitleFont);
    addPayload = addPayload.set('emailBodyFont', emailSettings.emailBodyFont);

    if (reviewGenType === Workflow.Internal) {
      addPayload = addPayload.set('buttonText', emailSettings.buttonText);
      addPayload = addPayload.set('buttonColor', emailSettings.buttonColor);
      addPayload = addToPayloadIfNotNull('buttonUrl', emailSettings.buttonUrl, addPayload);
    } else if (reviewGenType === Workflow.External) {
      addPayload = addPayload.set('buttonText', emailSettings.buttonText);
      addPayload = addPayload.set('buttonColor', emailSettings.buttonColor);
      addPayload = addToPayloadIfNotNull('buttonUrl', emailSettings.buttonUrl, addPayload);
      addPayload = addPayload.set(
        'includeDirectFeedbackOptionFlag',
        (emailSettings.includeDirectFeedbackOption ? emailSettings.includeDirectFeedbackOption : false).toString(),
      );
      addPayload = addToPayloadIfNotNull('directFeedbackMessage', emailSettings.directFeedbackMessage, addPayload);
    }

    return this.ajaxService.post(ApiRoutesV1.settingsEmailUpdate, addPayload.toString(), formUrlEncodedHttpOptions);
  }

  getEmailSettingDefaults(emailType: Workflow): Observable<EmailSettings> {
    return this.ajaxService
      .get(ApiRoutesV1.settingsEmailGetDefaults, {
        params: {
          emailType: reviewTypeMap[emailType],
        },
      })
      .pipe(map((response: HttpResponse<AjaxData>) => mapResponseToEmailSetting(response, emailType)));
  }

  getGreetingSelection(): Observable<string[]> {
    return this.ajaxService.get(ApiRoutesV1.settingsEmailGetGreetings).pipe(
      map((response: HttpResponse<AjaxData>) => {
        return response.body.data.greeting_options;
      }),
    );
  }

  getDefaultImages(): Observable<HttpResponse<AjaxData>> {
    return this.ajaxService.get(ApiRoutesV1.settingsEmailImage).pipe(
      map((response: HttpResponse<AjaxData>) => {
        return response;
      }),
    );
  }
}

function mapResponseToEmailSetting(response: HttpResponse<AjaxData>, emailType: Workflow): EmailSettings {
  const emailSettings: EmailSettings = {
    sendFromAddress: response.body.data.sendFromAddress,
    emailSubject: response.body.data.emailSubject,
    buttonText: '',
    buttonColor: '',
    buttonUrl: '',
    emailGreeting: response.body.data.emailGreeting,
    emailBody: response.body.data.messageBody,
    secondaryMessageBody: response.body.data.secondaryMessageBody,
    emailTitle: response.body.data.emailTitle,
    logoUrl: response.body.data.logoUrl,
    headerUrl: response.body.data.headerUrl,
    emailTitleFont: response.body.data.emailTitleFont,
    emailBodyFont: response.body.data.emailBodyFont,
    includeDirectFeedbackOption: false,
    directFeedbackMessage: '',
  };

  if (emailType === Workflow.Internal) {
    emailSettings.buttonText = response.body.data.buttonText;
    emailSettings.buttonColor = response.body.data.buttonColor;
    emailSettings.buttonUrl = response.body.data.buttonUrl;
  } else if (emailType === Workflow.External) {
    emailSettings.buttonText = response.body.data.buttonText;
    emailSettings.buttonColor = response.body.data.buttonColor;
    emailSettings.buttonUrl = response.body.data.buttonUrl;
    emailSettings.includeDirectFeedbackOption = response.body.data.includeDirectFeedbackOption || false;
    emailSettings.directFeedbackMessage = response.body.data.directFeedbackMessage;
  }
  return emailSettings;
}

export interface CloudStorageFile {
  fileUrl: string;
  filePath: string;
  fileName: string;
}

export interface EmailSettings {
  sendFromAddress: string;
  emailSubject: string;
  buttonText: string;
  buttonColor: string;
  buttonUrl: string;
  emailGreeting: string;
  emailBody: string;
  secondaryMessageBody: string;
  emailTitle: string;
  logoUrl: string;
  headerUrl: string;
  emailTitleFont: string;
  emailBodyFont: string;
  includeDirectFeedbackOption: boolean;
  directFeedbackMessage: string;
}
